import { api, qs } from './client.js';
import type {
  Paginated,
  Order,
  OrderDetail,
  LotteryCustomerPrizes,
  Product,
  ProductInput,
  ProductOption,
  Category,
  Prize,
  PrizeHistoryEntry,
  PrizeInput,
} from './types.js';

/** 주문 상세에서 고칠 수 있는 배송지 항목 (lottery_member 의 del_* 컬럼). */
export interface OrderShippingInput {
  name: string;
  phone: string;
  zipcode: string;
  address1: string;
  address2: string;
}

/** 원본 lottery_category_modify.php 의 수정 폼. server shared/category-crud.ts 의 categoryBody 와 짝이다. */
export interface CategoryInput {
  companyName: string;
  companyCode: string;
  jpName?: string;
  /** 1 = 굿즈, 2 = 오나홀 */
  itemType?: string;
  memo?: string;
}

/**
 * 원본 lottery/product/settings.php 의 설정 (lottery_config cf_id=1 한 줄).
 * server lottery/config.routes.ts · lotteryConfigBody 와 짝이다.
 */
export interface LotteryConfig {
  /** 교환기능 1 = 사용, 0 = 사용안함 */
  exchange: 0 | 1;
  exchangeNum: number;
  /** 폭탄구매 1 = 사용, 0 = 사용안함 */
  bakugai: 0 | 1;
  bakugaiNum: number;
  bannerImg: string;
}

export interface OrderFilters {
  page?: number;
  perPage?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  q?: string;
  kuji?: number | '';
  orderStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  includeTest?: boolean;
}

export const lotteryApi = {
  orders: (filters: OrderFilters) =>
    api.get<Paginated<Order>>(`/lottery/orders${qs({ ...filters })}`),

  order: (orderNo: string) =>
    api.get<OrderDetail>(`/lottery/orders/${encodeURIComponent(orderNo)}`),

  /** 원본 lottery_order_uu_list.php — 회원 한 명의 くじ 당첨 이력(randombox 의 customerPrizes 와 같은 자리). */
  customerPrizes: (memIdx: number) =>
    api.get<LotteryCustomerPrizes>(`/lottery/orders/customers/${memIdx}/prizes`),

  /**
   * 원본 lottery_excel_output.php (mode=excel) — 賞 단위 「くじ別集計」 엑셀.
   * 원본은 검색조건을 다시 넘겼지만, 여기서는 목록에서 체크한 주문번호만 넘긴다.
   */
  excelExportUrl: (orderNos: string[]) =>
    `/api/lottery/orders/export${qs({ orderNos: orderNos.join(',') })}`,

  /**
   * 원본 lottery_order_detail_update.php. 배송지(shipping)는 다섯 칸을 한 벌로
   * 보내거나 아예 빼야 한다 - 일부만 보내면 나머지가 빈 값으로 덮인다.
   */
  updateOrder: (
    orderNo: string,
    body: {
      orderStatus?: string;
      memo?: string;
      shipping?: OrderShippingInput;
      /** 경품별 배송취소·복구 (lottery_order_details.seq). randombox 와 같은 모양. */
      cancelSeqs?: number[];
      restoreSeqs?: number[];
    },
  ) =>
    api.patch<{
      orderNo: string;
      updated: boolean;
      affected: number;
      shippingSaved: boolean;
      canceled: number;
      restored: number;
    }>(`/lottery/orders/${encodeURIComponent(orderNo)}`, body),

  products: (filters: { page?: number; perPage?: number; q?: string; sort?: string; dir?: string; categoryCode?: string }) =>
    api.get<Paginated<Product>>(`/lottery/products${qs({ ...filters })}`),

  /** categoryCode 를 주면 그 카테고리에 속한 랜덤박스만 돌려준다. */
  productOptions: (categoryCode?: string) =>
    api.get<ProductOption[]>(`/lottery/products/options${qs({ categoryCode })}`),

  product: (lotteryId: number) => api.get<Product>(`/lottery/products/${lotteryId}`),

  /** 원본 product_modify.php 의 modifyexe. server shared/product-crud.ts 가 처리한다. */
  updateProduct: (lotteryId: number, body: ProductInput) =>
    api.patch<{ id: number }>(`/lottery/products/${lotteryId}`, body),

  /**
   * 원본 product_copy.php: 값을 미리 채운 폼으로 새 상품을 만들고, 원본 상품의
   * 경품(lottery_prizes)도 새 상품 밑으로 복제한 뒤 총확률을 다시 계산한다.
   */
  copyProduct: (lotteryId: number, body: ProductInput) =>
    api.post<{
      id: number;
      sourceId: number;
      copiedChildren: number;
      childCopyFailed: boolean;
      probabilityRecalcFailed: boolean;
    }>(
      `/lottery/products/${lotteryId}/copy`,
      body,
    ),

  categories: (filters: { page?: number; perPage?: number; q?: string; sort?: string; dir?: string }) =>
    api.get<Paginated<Category>>(`/lottery/categories${qs({ ...filters })}`),

  /** 원본 lottery_category_add.php. server/shared/category-crud.ts 가 처리한다. */
  createCategory: (body: CategoryInput) =>
    api.post<{ seq: number }>('/lottery/categories', body),

  /** 원본 lottery_category_modify.php 의 modifyexe. server/shared/category-crud.ts 가 처리한다. */
  updateCategory: (seq: number, body: CategoryInput) =>
    api.patch<{ seq: number }>(`/lottery/categories/${seq}`, body),

  /** 원본 product/settings.php - 랜덤박스 목록 위 '설정'. */
  config: () => api.get<LotteryConfig>('/lottery/config'),

  /** 원본 product/settings.php 의 modifyexe. */
  updateConfig: (body: LotteryConfig) => api.patch<LotteryConfig>('/lottery/config', body),

  prizes: (lotteryId: number) =>
    api.get<{ lotteryId: number; items: Prize[] }>(`/lottery/products/${lotteryId}/prizes`),

  updatePrizeQuantity: (lotteryId: number, prizeId: number, quantity: number) =>
    api.patch<{ quantity: number; previousQuantity?: number; changed: boolean }>(
      `/lottery/products/${lotteryId}/prizes/${prizeId}/quantity`,
      { quantity },
    ),

  prizeHistory: (lotteryId: number, prizeId: number) =>
    api.get<PrizeHistoryEntry[]>(`/lottery/products/${lotteryId}/prizes/${prizeId}/history`),

  /** 원본 prize_image_modify.php: 목록의 '이미지URL' 칸에서 썸네일 한 칸만 바꾼다. */
  updatePrizeThumbnail: (lotteryId: number, prizeId: number, thumbnail: string) =>
    api.patch<{ prizeId: number; thumbnail: string }>(
      `/lottery/products/${lotteryId}/prizes/${prizeId}/thumbnail`,
      { thumbnail },
    ),

  /** 원본 prize_modify.php 의 modifyexe. */
  updatePrize: (lotteryId: number, prizeId: number, body: PrizeInput) =>
    api.patch<{ prizeId: number }>(`/lottery/products/${lotteryId}/prizes/${prizeId}`, body),

  /** 원본 prize_copy.php: 기존 경품 값을 미리 채운 폼으로 새 경품을 등록한다 — 등록과 같은 엔드포인트. */
  createPrize: (lotteryId: number, body: PrizeInput) =>
    api.post<{ prizeId: number }>(`/lottery/products/${lotteryId}/prizes`, body),

  /** 원본 prize_modify.php 의 delexe. */
  deletePrize: (lotteryId: number, prizeId: number) =>
    api.del<{ prizeId: number; deleted: boolean }>(`/lottery/products/${lotteryId}/prizes/${prizeId}`),

  // ── 배송 처리 (원본 lottery_excel_output_delivery.php · delivery_excel_check*) ──

  /**
   * 배송처리용 — CJ대한통운 배송업로드 CSV.
   *
   * 원본과 같이 **체크한 주문이 아니라 조건에 걸린 전부**가 대상이다. 한 사람의
   * 주문 여러 건을 배송 한 줄로 묶기 때문에, 일부만 골라 뽑으면 그 사람 물품이
   * 빠진 송장이 나간다.
   */
  cjExportUrl: (p: {
    kuji?: number | '';
    q?: string;
    orderby?: string;
    order?: string;
  }) => `/api/lottery/delivery/cj-export${qs({ ...p })}`,

  /**
   * 비즈호스트 등록용 엑셀 — 랜덤박스 주문의 비즈호스트 등록용과 같은 양식.
   * 배송처리용과 같이 체크한 주문이 아니라 조건(랜덤박스 · 검색어)에 걸린 전부가 대상이다.
   */
  bizhostExportUrl: (p: { kuji?: number | ''; q?: string }) =>
    `/api/lottery/delivery/bizhost-export${qs({ ...p })}`,

  trackingPending: (kuji?: number | '') =>
    api.get<{ seq: number; hphone: string }[]>(`/lottery/delivery/tracking/pending${qs({ kuji })}`),

  applyTracking: (body: {
    kuji?: number | '';
    updates: { hphone: string; trackingNumber: string }[];
  }) =>
    api.post<{ success: boolean; updatedCount: number; totalUpdates: number }>(
      '/lottery/delivery/tracking/apply',
      body,
    ),
};
